import { useTranslation } from 'react-i18next'
import type { ProductCard as ProductCardType } from '../../types'
import ProductCard from './ProductCard'

interface ProductGridProps {
  products: ProductCardType[]
  isLoading: boolean
  searchQuery?: string
  /** Href for the "clear search" CTA. Required for the CTA to appear. */
  clearSearchHref?: string
}

function SkeletonCard() {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden animate-pulse">
      <div className="aspect-square bg-neutral-200" />
      <div className="p-4 flex flex-col gap-2">
        <div className="h-3 w-24 bg-neutral-200 rounded" />
        <div className="h-4 w-full bg-neutral-200 rounded" />
        <div className="h-3 w-20 bg-neutral-200 rounded" />
        <div className="h-3 w-full bg-neutral-200 rounded" />
        <div className="h-3 w-3/4 bg-neutral-200 rounded" />
        <div className="flex gap-1 mt-2">
          <div className="h-5 w-16 bg-neutral-200 rounded-full" />
        </div>
        <div className="h-5 w-20 bg-neutral-200 rounded mt-2 pt-2 border-t border-neutral-100" />
      </div>
    </div>
  )
}

export default function ProductGrid({ products, isLoading, searchQuery, clearSearchHref }: ProductGridProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-neutral-400 text-center px-4">
        <svg className="h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <p className="text-lg font-medium text-neutral-700">
          {searchQuery
            ? t('product.noResultsForQuery', { query: searchQuery })
            : t('product.noResults')}
        </p>
        <p className="text-sm mt-1 mb-6">{t('product.noResultsHint')}</p>
        <div className="flex flex-wrap justify-center gap-3">
          {searchQuery && clearSearchHref && (
            <a
              href={clearSearchHref}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              {t('product.clearSearch')}
            </a>
          )}
          <a
            href="/products"
            className="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 text-sm font-medium hover:bg-neutral-50 transition-colors"
          >
            {t('product.browseAll')}
          </a>
          <a
            href="/contact"
            className="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 text-sm font-medium hover:bg-neutral-50 transition-colors"
          >
            {t('nav.contact')}
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {products.map(product => (
        <ProductCard key={product.uuid} product={product} />
      ))}
    </div>
  )
}
